import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

export const RoleSchema = z.enum(['ADMIN', 'USER']);
export type Role = z.infer<typeof RoleSchema>;

export const AccountRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: RoleSchema,
  modules: z.array(z.string()),
  kcUserId: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  hasLocalPassword: z.boolean(),
  /**
   * ZAWĘŻENIE DO KLIENTA. `null` = konto SDI, widzi wszystkich klientów.
   *
   * Do 4.11 tego pola nie było i każde konto widziało wszystkie wyspy. Zakresu nie da się
   * zmienić edycją — przeniesienie konta między klientami to zmiana uprawnień, nie edycja pola.
   */
  clientId: z.string().nullable(),
  clientName: z.string().nullable(),
});

export type AccountRow = z.infer<typeof AccountRowSchema>;

const AccountsPageSchema = z.object({
  rows: z.array(AccountRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export type AccountsPage = z.infer<typeof AccountsPageSchema>;

export type ListAccountsParams = {
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
  includeArchived: boolean;
};

function toQuery(p: ListAccountsParams): string {
  const q = new URLSearchParams({
    page: String(p.page),
    pageSize: String(p.pageSize),
    sort: p.sort,
    dir: p.dir,
    includeArchived: String(p.includeArchived),
  });
  if (p.search) q.set('search', p.search);
  return q.toString();
}

export const listAccounts = (p: ListAccountsParams) => apiGet(`/accounts?${toQuery(p)}`, AccountsPageSchema);

/**
 * Katalog uprawnień + SUFIT konta klienta.
 *
 * `clientAllowed` przychodzi z serwera, bo to ta sama lista, którą egzekwuje `ModulesGuard`
 * (§8a: klient tylko raportowo). Wpisana na froncie rozjechałaby się przy pierwszej zmianie
 * i ekran oferowałby checkboxy, których nadanie serwer odrzuca.
 */
export const fetchModuleCatalog = () =>
  apiGet(
    '/accounts/modules',
    z.object({
      modules: z.array(z.string()),
      clientAllowed: z.array(z.string()),
      /** sufit tokenu integracyjnego — z niego rysuje zakres ekran tokenów maszynowych */
      integrationAllowed: z.array(z.string()),
    }),
  );

/**
 * Zakresy, w jakich to konto wolno założyć.
 *
 * `allowSdi=false` znaczy, że pytający sam jest zawężony do klienta i nie może utworzyć konta
 * bez zawężenia — lista wyboru nie ma wtedy podpowiadać wyjścia z zakresu, choćby serwer i tak
 * takie żądanie odrzucił.
 */
export const fetchAccountScopes = () =>
  apiGet(
    '/accounts/scopes',
    z.object({ allowSdi: z.boolean(), clients: z.array(z.object({ id: z.string(), name: z.string() })) }),
  );

export type CreateAccountPayload = {
  email: string;
  name?: string;
  role: Role;
  modules: string[];
  password?: string;
  /** WYMAGANE, bez wartości domyślnej — `null` („konto SDI") musi być wyborem, nie pominięciem. */
  clientId: string | null;
};

export const createAccount = (payload: CreateAccountPayload) => apiSend('POST', '/accounts', payload, AccountRowSchema);

export type UpdateAccountPayload = {
  name?: string | null;
  role?: Role;
  modules?: string[];
  active?: boolean;
};

export const updateAccount = (id: string, payload: UpdateAccountPayload) =>
  apiSend('PATCH', `/accounts/${id}`, payload, AccountRowSchema);

export const setAccountPassword = (id: string, password: string) =>
  apiSend('POST', `/accounts/${id}/password`, { password }, z.object({ ok: z.literal(true) }));
